import { useEffect, useSyncExternalStore } from 'react';
import { getUser, hasSession, netStore, refreshSession } from '../lib/api.ts';
import { discard, flushQueue, hasPending, useQueue } from '../lib/offline.ts';
import { invalidate } from '../lib/query.ts';
import { Button, toast } from './ui.tsx';

const RETRY_MS = 20_000;
// after this long in the background the data on screen is refetched when the app comes back
const STALE_MS = 60_000;

/** restore the session if it was lost to the network, refresh the screen, send waiting operations */
async function reconnect(refresh = false) {
  if (!hasSession() || !netStore.get()) {
    if (!(await refreshSession())) return;
    refresh = true;
  }
  if (refresh) invalidate();
  const sent = await flushQueue();
  if (sent) toast(sent === 1 ? 'Отложенная операция отправлена' : `Отправлено отложенных операций: ${sent}`);
}

/** keeps a home-screen app in sync: on start, on regained network, on return from background */
export function useReconnect() {
  const online = useSyncExternalStore(netStore.subscribe, netStore.get);
  const queue = useQueue();

  useEffect(() => {
    void reconnect();
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      void reconnect(hiddenAt > 0 && Date.now() - hiddenAt > STALE_MS);
    };
    const onOnline = () => void reconnect();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  const waiting = !online || hasPending();
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => void reconnect(), RETRY_MS);
    return () => clearInterval(t);
  }, [waiting, queue]);
}

export function OfflineBar() {
  const online = useSyncExternalStore(netStore.subscribe, netStore.get);
  const queue = useQueue();
  const userId = getUser()?.id;
  const mine = queue.filter((p) => p.userId === userId);
  const pending = mine.filter((p) => !p.error);
  const failed = mine.filter((p) => p.error);
  if (online && !mine.length) return null;

  const shown = pending.slice(0, 3).map((p) => p.label).join('; ');
  return (
    <div className="offline-bar" role="status">
      {!online ? <p>Нет связи с сервером — показаны сохранённые данные.</p> : null}
      {pending.length ? (
        <div className="offline-row">
          <span>
            Ждут отправки: {pending.length} — {shown}{pending.length > 3 ? '…' : ''}
          </span>
          {online ? <Button size="sm" onClick={() => void reconnect()}>Отправить</Button> : null}
        </div>
      ) : null}
      {failed.map((p) => (
        <div key={p.clientId} className="offline-row offline-failed">
          <span>Сервер не принял «{p.label}»: {p.error}</span>
          <Button size="sm" variant="ghost" onClick={() => discard(p.clientId)}>Убрать</Button>
        </div>
      ))}
    </div>
  );
}
